import { LoadingButton } from "@mui/lab"
import { Box, Card, CircularProgress, Stack, TextField, Typography } from "@mui/material"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { useGetAssistantMessagesQuery, useSendAssistantChatMutation } from "../../core/api/assistant-api"

const isChatMessage = (message) =>
    message?.messageType === "USER" || message?.messageType === "ASSISTANT"

const MessageBubble = ({ message }) => {
    const isUser = message.messageType === "USER"

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: isUser ? "flex-end" : "flex-start"
            }}
        >
            <Box
                sx={{
                    borderRadius: 1,
                    maxWidth: "75%",
                    px: 2,
                    py: 1.25,
                    bgcolor: isUser ? "primary.main" : "grey.300",
                    color: isUser ? "primary.contrastText" : "text.primary",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word"
                }}
            >
                <Typography variant="body2" component="div">
                    {message.text}
                </Typography>
            </Box>
        </Box>
    )
}

export default function AssistantChat() {
    const { game } = useParams()
    const [input, setInput] = useState("")
    const [pendingUserMessage, setPendingUserMessage] = useState(null)
    const bottomRef = useRef(null)

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error
    } = useGetAssistantMessagesQuery(game, { skip: !game })

    const [sendChat, { isLoading: isSending }] = useSendAssistantChatMutation()

    const messages = useMemo(() => {
        const history = Array.isArray(data) ? data.filter(isChatMessage) : []
        if (pendingUserMessage) {
            return [...history, pendingUserMessage]
        }
        return history
    }, [data, pendingUserMessage])

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" })
    }, [messages, isSending])

    const canSend = Boolean(input.trim()) && !isSending && Boolean(game)

    const handleSend = async () => {
        const message = input.trim()
        if (!message || !canSend) return

        setPendingUserMessage({ messageType: "USER", text: message })
        setInput("")

        try {
            await sendChat({ game, message }).unwrap()
        } catch (_) {
            setInput(message)
        } finally {
            setPendingUserMessage(null)
        }
    }

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault()
            handleSend()
        }
    }

    return (
        <Card sx={{ height: "88vh", display: "flex", flexDirection: "column" }}>
            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    p: 2,
                    minHeight: 0
                }}
            >
                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress size={32}/>
                    </Box>
                ) : isError ? (
                    <Typography color="error" sx={{ py: 2 }}>
                        Failed to load messages{error?.status ? ` (${error.status})` : ""}
                    </Typography>
                ) : messages.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                        Эксперементальный AI-Ассистент для помощи в поиске информации
                        <br />и совершения действий со стэйтом через чат.<br />
                        Напишите ваш вопрос.
                    </Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {messages.map((message, index) => (
                            <MessageBubble key={`${message.messageType}-${index}-${message.text?.slice(0, 24)}`}
                                           message={message}/>
                        ))}
                        {isSending && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 1 }}>
                                <CircularProgress size={16}/>
                                <Typography variant="body2" color="text.secondary">
                                    Assistant is thinking…
                                </Typography>
                            </Box>
                        )}
                        <div ref={bottomRef}/>
                    </Stack>
                )}
                {!isLoading && isFetching && !isSending && (
                    <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
                        <CircularProgress size={18}/>
                    </Box>
                )}
            </Box>

            <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        maxRows={6}
                        minRows={1}
                        placeholder="Type a message…"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isSending}
                    />
                    <LoadingButton
                        size="small"
                        variant="contained"
                        loading={isSending}
                        disabled={!canSend}
                        onClick={handleSend}
                        sx={{ minWidth: 96 }}
                    >
                        Send
                    </LoadingButton>
                </Stack>
            </Box>
        </Card>
    )
}
